import { DateTime } from 'luxon';

export interface ICookie {
  name: string;
  value: string;
}

export class ServerCookies {
  constructor(public readonly cookies: Record<string, ICookie[]> = {}) {}

  getValueOf(name: string): string | undefined {
    const cookie = this.cookies[name];
    if (
      !cookie ||
      cookie.length === 0 ||
      !cookie[0].value ||
      cookie[0].value === 'null'
    ) {
      return undefined;
    }
    return cookie[0].value as string;
  }

  static trimAndUnescape(value?: string) {
    if (!value) {
      return value;
    }
    return decodeURIComponent(value.trim());
  }

  static fromCookieObjects(cookies: ICookie[]): ServerCookies {
    const cookiesMap: Record<string, ICookie[]> = {};

    cookies.forEach((cookie) => {
      if (!cookiesMap[cookie.name]) {
        cookiesMap[cookie.name] = [];
      }
      cookiesMap[cookie.name].push(cookie);
    });

    return new ServerCookies(cookiesMap);
  }

  /**
   * Mostly intended for use during testing - this allows you to create an object from a simple record of name to value.
   *
   * The reason to NOT use this most of the time is that it doesn't allow for multiple cookies with the same name,
   * which is something that we expect to happen in the real world.
   * @param cookies
   */
  static fromRecord(cookies: Record<string, string>): ServerCookies {
    const cookieObjects: ICookie[] = Object.entries(cookies).map(
      ([name, value]) => ({ name, value }),
    );
    return ServerCookies.fromCookieObjects(cookieObjects);
  }

  static parse(cookie: string): ServerCookies {
    const cookies: ICookie[] = [];
    if (!cookie || cookie === '') {
      return new ServerCookies({});
    }

    cookie.split(';').forEach((c) => {
      const [name, value] = c.split('=');
      const trimmedName = ServerCookies.trimAndUnescape(name);
      const trimmedValue = ServerCookies.trimAndUnescape(value);

      if (!trimmedName) {
        return; // Skip this one - it's not a cookie
      }

      cookies.push({ name: trimmedName, value: trimmedValue || '' });
    });

    return ServerCookies.fromCookieObjects(cookies);
  }

  static createCookiesStrings(
    cookies: Record<string, string | null>,
  ): string[] {
    return Object.keys(cookies).map(
      (key) =>
        `${key}=${cookies[key] === null ? '' : cookies[key]}; Path=/; HttpOnly;`,
    );
  }

  /** Serialize cookies into a single `Cookie` header string.
   *  When `names` is provided, only cookies whose name is in the list are included. */
  toCookieHeader(names?: string[]): string {
    const all = Object.values(this.cookies).flat();
    const filtered = names ? all.filter((c) => names.includes(c.name)) : all;
    return filtered.map((c) => `${c.name}=${c.value}`).join('; ');
  }
}

export class WriteableServerCookies extends ServerCookies {
  private readonly expiries = new Map<string, DateTime>();
  constructor(serverCookies: ServerCookies = new ServerCookies()) {
    super(serverCookies.cookies);
  }

  public setExpiry(name: string, expiry: DateTime) {
    this.expiries.set(name, expiry);
  }

  public getExpiry(name: string): DateTime | undefined {
    return this.expiries.get(name);
  }

  public clear(name: string) {
    // We assume an empty cookie should be unset
    this.cookies[name] = [];
  }

  public set(name: string, value: string) {
    this.cookies[name] = [{ name, value }];
  }

  createCookiesStrings(): string[] {
    const cookiesStrings: string[] = [];
    for (const cookieKey in this.cookies) {
      if (this.cookies[cookieKey].length === 0) {
        cookiesStrings.push(
          `${cookieKey}=; Path=/; HttpOnly; Expires=Thu, 01 Jan 1970 00:00:00 GMT`,
        );
      } else {
        for (const cookie of this.cookies[cookieKey]) {
          const expiry = this.expiries.get(cookieKey);
          if (expiry) {
            cookiesStrings.push(
              `${cookieKey}=${cookie.value}; Path=/; HttpOnly; Expires=${expiry.toHTTP()}`,
            );
          } else {
            cookiesStrings.push(
              `${cookieKey}=${cookie.value}; Path=/; HttpOnly;`,
            );
          }
        }
      }
    }

    return cookiesStrings;
  }

  /**
   * Just generates a cookies string as we'd receive from a request. This is mostly useful for testing
   */
  createCookiesForRequest(): string {
    const cookiesStrings: string[] = [];
    for (const cookieKey in this.cookies) {
      if (this.cookies[cookieKey].length === 0) {
        cookiesStrings.push(`${cookieKey}=`); // Empty cookie??
      } else {
        for (const cookie of this.cookies[cookieKey]) {
          cookiesStrings.push(`${cookieKey}=${cookie.value}`);
        }
      }
    }

    return cookiesStrings.join(';');
  }

  /**
   * Generates the (incorrect) cookie format used by NextRequest - should only really be used in testing.
   * This is dangerous as we can overwrite cookies when there are two with the same name
   */
  dangerouslyExportAsRecord(): Record<string, string> {
    const cookiesRecords: Record<string, string> = {};
    for (const cookie in this.cookies) {
      cookiesRecords[cookie] = this.cookies[cookie][0].value;
    }
    return cookiesRecords;
  }
}
